<template>
  <div
    v-if="visible"
    class="ui-alert text-gray-80 relative border-l"
    :class="[
      brandingClasses,
      fullWidth && 'w-full',
      small && 'rounded-sm border-l-4 py-[0.4375rem] pl-3 text-xs',
      !small && 'rounded border-l-[6px] py-[0.84375rem] pl-[1.125rem] text-sm',
      small && closable && 'pr-11',
      !small && closable && 'pr-16',
      !closable && 'pr-3',
    ]"
  >
    <slot />
    <button
      v-if="closable"
      type="button"
      :class="`ui-alert__trigger absolute h-5 w-5 ${closeButtonPositionClass}`"
      @click="closeAlert"
    >
      <x-icon class="icon-s" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { camelCase, upperFirst, kebabCase } from 'lodash-es'
import { XIcon } from '../icons'

interface AlertProps {
  // brand props
  success?: boolean
  info?: boolean
  warning?: boolean
  error?: boolean
  // other props
  fullWidth?: boolean
  closable?: boolean
  small?: boolean
  cornerPosCloseButton?: boolean
  storageSuffix?: string
}

const props = withDefaults(defineProps<AlertProps>(), {
  storageSuffix: '',
  cornerPosCloseButton: false,
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const getKey = (suffix?: string) => (suffix ? `KlausUiAlert${upperFirst(camelCase(suffix))}` : undefined)

const visible = ref(true)
const sessionStorageKey = getKey(props.storageSuffix)
const closeButtonPositionClass = computed(() =>
  props.cornerPosCloseButton ? 'top-4 right-6' : 'right-3 top-1/2 -translate-y-1/2',
)

onMounted(() => {
  if (!sessionStorageKey) return
  const dismissed = sessionStorage.getItem(sessionStorageKey) === 'true'
  if (dismissed) {
    visible.value = false
    emit('close')
  }
})

const closeAlert = () => {
  if (sessionStorageKey) {
    sessionStorage.setItem(sessionStorageKey, 'true')
    visible.value = false
  }
  emit('close')
}

const brandingProps = ['success', 'info', 'warning', 'error']
const brandingClasses = computed(() =>
  brandingProps.filter((key) => key in props && props[key] === true).map((key) => `-${kebabCase(key)}`),
)
</script>

<style lang="scss">
.ui-alert {
  background-color: var(--alert-bg, var(--gray-10));
  border-left-color: var(--alert-border, var(--gray-30));

  button {
    display: inline-block;
  }

  &.-success {
    --alert-border: var(--green-30);
    --alert-bg: var(--green-10);
    --alert-trigger-color: var(--green-70);
  }

  &.-info {
    --alert-border: var(--blue-30);
    --alert-bg: var(--blue-10);
    --alert-trigger-color: var(--blue-70);
  }

  &.-warning {
    --alert-border: var(--orange-30);
    --alert-bg: var(--orange-10);
    --alert-trigger-color: var(--orange-70);
  }

  &.-error {
    --alert-border: var(--red-30);
    --alert-bg: var(--red-10);
    --alert-trigger-color: var(--red-70);
  }

  &__trigger {
    color: var(--alert-trigger-color, var(--gray-70));
  }
}
</style>
